import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { classNames } from 'primereact/utils';
import moment from 'moment';

const RATING_OPTIONS = ['Excellent', 'Good', 'Average', 'Satisfactory', 'Not Satisfactory'].map((v) => ({ label: v, value: v }));

const RATING_FIELDS = [
    { name: 'Food_quality', label: 'Quality of food', icon: 'pi pi-star' },
    { name: 'cleanliness', label: 'Cleanliness', icon: 'pi pi-verified' },
    { name: 'staff_behaviour', label: 'Staff behaviour', icon: 'pi pi-users' },
    { name: 'services', label: 'Speed of service', icon: 'pi pi-clock' },
    { name: 'food_option_available', label: 'Food options', icon: 'pi pi-list' },
    { name: 'menusoption', label: 'Menu variety', icon: 'pi pi-book' },
    { name: 'affordabality', label: 'Affordability', icon: 'pi pi-wallet' },
    { name: 'expericenceoption', label: 'Overall experience', icon: 'pi pi-heart' }
];

const SectionDivider = ({ icon, title }) => (
    <div className="fb-section-divider">
        <i className={icon} />
        <span>{title}</span>
    </div>
);

const FieldLabel = ({ children, required }) => (
    <label className="fb-field-label">
        {children} {required && <span className="fb-required">*</span>}
    </label>
);

export default function FeedbackDialog({ visible, onHide, feedbackData, onSave, saving }) {
    const [feedback, setFeedback] = useState(feedbackData);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (feedbackData) {
            const data = { ...feedbackData };
            const parsedDate = data.feedback_date && moment(data.feedback_date, 'YYYY-MM-DD', true).isValid() ? moment(data.feedback_date).toDate() : new Date();
            data.feedback_date = parsedDate;
            setFeedback(data);
            setSubmitted(false);
        }
    }, [feedbackData]);

    if (!feedback) return null;

    const isEdit = !!feedback.feedback_id;

    const onInputChange = (val, name) => {
        if (name === 'phone_number' && val && !/^\d{0,10}$/.test(val)) return;
        setFeedback((prev) => ({ ...prev, [name]: val }));
    };

    const isInvalid = (name) => {
        if (!submitted) return false;
        if (name === 'phone_number') return feedback.phone_number && feedback.phone_number.length !== 10;
        return false;
    };

    const handleSave = () => {
        setSubmitted(true);
        const formats = !feedback.phone_number || feedback.phone_number.length === 10;
        if (!formats) return;
        onSave({
            ...feedback,
            feedback_date: moment(feedback.feedback_date).format('YYYY-MM-DD')
        });
    };

    const dialogHeader = (
        <div className="fb-dialog-header">
            <span className="fb-dialog-icon">
                <i className="pi pi-comments" />
            </span>
            <div>
                <div className="fb-dialog-title">FeedBack Details</div>
                <div className="fb-dialog-subtitle">{isEdit ? 'Update this feedback record' : 'Capture patient food-court feedback'}</div>
            </div>
        </div>
    );

    const dialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancel" icon="pi pi-times" outlined severity="secondary" onClick={onHide} style={{ minWidth: '100px' }} />
            <Button label="Save" icon="pi pi-check" severity="success" onClick={handleSave} loading={saving} style={{ minWidth: '100px' }} />
        </div>
    );

    return (
        <Dialog
            visible={visible}
            style={{ width: '760px' }}
            breakpoints={{ '960px': '92vw', '640px': '96vw' }}
            header={dialogHeader}
            modal
            className="p-fluid fb-dialog"
            footer={dialogFooter}
            onHide={onHide}
            contentStyle={{ padding: '1rem 1.5rem 0.5rem' }}
        >
            <SectionDivider icon="pi pi-id-card" title="Patient Information" />
            <div className="grid" style={{ margin: 0 }}>
                <div className="field col-12 md:col-6 fb-field">
                    <FieldLabel>Date</FieldLabel>
                    <Calendar value={feedback.feedback_date} dateFormat="dd-mm-yy" disabled className="w-full" />
                </div>
                <div className="field col-12 md:col-6 fb-field">
                    <FieldLabel>Room No</FieldLabel>
                    <InputText value={feedback.roomno || ''} onChange={(e) => onInputChange(e.target.value, 'roomno')} placeholder="Enter room no" />
                </div>
                <div className="field col-12 md:col-6 fb-field">
                    <FieldLabel>Bed No</FieldLabel>
                    <InputText value={feedback.bedno || ''} onChange={(e) => onInputChange(e.target.value, 'bedno')} placeholder="Enter bed no" />
                </div>
                <div className="field col-12 md:col-6 fb-field">
                    <FieldLabel>Patient name</FieldLabel>
                    <InputText value={feedback.customername || ''} onChange={(e) => onInputChange(e.target.value, 'customername')} placeholder="Enter patient name" />
                </div>
                <div className="field col-12 md:col-6 fb-field">
                    <FieldLabel>Mobile No</FieldLabel>
                    <InputText
                        value={feedback.phone_number || ''}
                        onChange={(e) => onInputChange(e.target.value, 'phone_number')}
                        placeholder="10-digit number"
                        keyfilter="num"
                        maxLength={10}
                        className={classNames({ 'p-invalid': isInvalid('phone_number') })}
                    />
                    {isInvalid('phone_number') && <small className="p-error">Must be exactly 10 digits.</small>}
                </div>
                <div className="field col-12 md:col-6 fb-field">
                    <FieldLabel>Email ID</FieldLabel>
                    <InputText value={feedback.email_id || ''} onChange={(e) => onInputChange(e.target.value, 'email_id')} placeholder="Enter email id" />
                </div>
            </div>

            <SectionDivider icon="pi pi-star-fill" title="Rate Your Experience" />
            <div className="grid" style={{ margin: 0 }}>
                {RATING_FIELDS.map((f) => (
                    <div key={f.name} className="field col-12 md:col-6 fb-field">
                        <FieldLabel>{f.label}</FieldLabel>
                        <Dropdown
                            value={feedback[f.name] || null}
                            options={RATING_OPTIONS}
                            onChange={(e) => onInputChange(e.value, f.name)}
                            optionLabel="label"
                            optionValue="value"
                            placeholder={f.label}
                        />
                    </div>
                ))}
            </div>

            <SectionDivider icon="pi pi-comment" title="Comments & Appreciation" />
            <div className="grid" style={{ margin: 0 }}>
                <div className="field col-12 fb-field">
                    <FieldLabel>Comments</FieldLabel>
                    <InputTextarea value={feedback.comments || ''} onChange={(e) => onInputChange(e.target.value, 'comments')} rows={2} placeholder="Any additional comments..." />
                </div>
                <div className="field col-12 fb-field">
                    <FieldLabel>Would you like to appreciate any of ur staff?</FieldLabel>
                    <InputTextarea value={feedback.appreciate || ''} onChange={(e) => onInputChange(e.target.value, 'appreciate')} rows={2} placeholder="Write your appreciation here..." />
                </div>
            </div>
        </Dialog>
    );
}
